import * as Sentry from "@sentry/react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SimpleRegistrationModal } from "@/components/SimpleRegistrationModal";
import MetaPixel from "@/components/MetaPixel";

const NotFound = lazy(() => import("@/pages/not-found"));
const Landing = lazy(() => import("@/pages/Landing"));
const Webinar = lazy(() => import("@/pages/Webinar"));
const About = lazy(() => import("@/pages/About"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
// DEPRECATED: Clarity Session page disabled per client (Mar 12, 2026). File preserved for reference.
// const ThankYouClarity = lazy(() => import("@/pages/ThankYouClarity"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const ActiveCampaignConfig = lazy(() => import("@/pages/ActiveCampaignConfig"));

function URLNormalizer() {
  const [location, setLocation] = useLocation();
  
  useEffect(() => {
    if (/^\/(?:index(?:\.html?)?)\/?$/i.test(location)) {
      console.log("Normalizing URL from", location, "to /");
      setLocation("/", { replace: true });
    }
  }, [location, setLocation]);
  
  return null;
}

function Router() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-lg">Loading...</div>
      </div>
    }>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/about" component={About} />
        <Route path="/webinar-completed" component={ThankYou} />
        {/* DEPRECATED: Clarity Session page — redirects to paid Strategy Session page */}
        <Route path="/webinar-completed-clarity">
          {() => { window.location.replace('/webinar-completed'); return null; }}
        </Route>
        <Route path="/webinar/:id" component={Webinar} />
        <Route path="/webinar" component={Webinar} />
        <Route path="/admin" component={AdminLogin} />
        <Route path="/admin/dashboard" component={Admin} />
        <Route path="/admin/activecampaign-config" component={ActiveCampaignConfig} />
        <Route path="*" component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              We're sorry for the inconvenience. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <div className="min-h-screen flex flex-col">
            <URLNormalizer />
            <MetaPixel />
            <Navbar />
            <div className="flex-grow">
              <Router />
            </div>
            <Footer />
            {/* Global registration modal */}
            <SimpleRegistrationModal />
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
